// ForgeTrack — Technical Uncertainty & CPA Review
// Route: /technical-uncertainty

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, { GlassPanel, EmptyState, LoadingShimmer, StatusBadge, SectionLabel } from '../components/engine/EnginePageShell';
import {
  useProjectList,
  useTechnicalUncertainty,
  useTechnicalUncertaintyMutations,
  type TechnicalUncertainty,
} from '../lib/api/hooks-extended';

export const Route = createFileRoute('/technical-uncertainty')({
  component: TechnicalUncertaintyPage,
});

function TechnicalUncertaintyPage() {
  const { data: projects } = useProjectList();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const { data: items, loading, refetch } = useTechnicalUncertainty(selectedProjectId);
  const { upsert, markCpaReviewed, loading: mutating } = useTechnicalUncertaintyMutations();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editScore, setEditScore] = useState(0);
  const [editRationale, setEditRationale] = useState('');

  const startEdit = (item: TechnicalUncertainty) => {
    setEditingId(item.workItemId);
    setEditScore(item.uncertaintyScore ?? 3);
    setEditRationale(item.uncertaintyRationale ?? '');
  };

  const saveEdit = async () => {
    if (editingId === null) return;
    const ok = await upsert({ workItemId: editingId, uncertaintyScore: editScore, uncertaintyRationale: editRationale });
    if (ok) { setEditingId(null); refetch(); }
  };

  const handleCpaReview = async (workItemId: number) => {
    const ok = await markCpaReviewed(workItemId);
    if (ok) refetch();
  };

  const reviewed = items?.filter(i => i.cpaReviewed).length ?? 0;
  const total = items?.length ?? 0;
  const avgScore = total > 0 ? (items!.reduce((s, i) => s + (i.uncertaintyScore ?? 0), 0) / total).toFixed(1) : '—';

  return (
    <EnginePageShell title="Technical Uncertainty" icon="🔬" accentColor={C.violet}>
      {/* Project selector */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <SectionLabel color={C.violet}>Project</SectionLabel>
        <select
          value={selectedProjectId ?? ''}
          onChange={(e) => setSelectedProjectId(e.target.value ? Number(e.target.value) : null)}
          style={{
            padding: '8px 14px', borderRadius: 8, fontSize: 13,
            background: C.panel, color: C.text, border: `1px solid ${C.faint}`,
            fontFamily: F.display, outline: 'none', minWidth: 240,
          }}
        >
          <option value="">Select a project...</option>
          {projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {!selectedProjectId && (
        <EmptyState icon="🔬" title="Select a project" subtitle="Choose a project to view and manage technical uncertainty scores for its work items." />
      )}

      {selectedProjectId && loading && <LoadingShimmer />}

      {selectedProjectId && !loading && items && (
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
            <GlassPanel accent={C.violet} style={{ padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.violet, fontFamily: F.mono }}>{total}</div>
              <div style={{ fontSize: 10, color: C.muted }}>Work Items</div>
            </GlassPanel>
            <GlassPanel accent={C.neon} style={{ padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.neon, fontFamily: F.mono }}>{reviewed}/{total}</div>
              <div style={{ fontSize: 10, color: C.muted }}>CPA Reviewed</div>
            </GlassPanel>
            <GlassPanel accent={C.amber} style={{ padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.amber, fontFamily: F.mono }}>{avgScore}</div>
              <div style={{ fontSize: 10, color: C.muted }}>Avg Uncertainty</div>
            </GlassPanel>
          </div>

          {items.length === 0 ? (
            <EmptyState icon="🔬" title="No work items" subtitle="This project has no work items to score." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((item) => (
                <GlassPanel key={item.workItemId} accent={item.cpaReviewed ? C.neon : C.faint} style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: F.display, marginBottom: 4 }}>
                        {item.workItemTitle}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
                        Score: {item.uncertaintyScore ?? '—'}/5
                        {item.uncertaintyRationale && ` · ${item.uncertaintyRationale.substring(0, 100)}...`}
                      </div>
                      {item.cpaReviewed && (
                        <StatusBadge color={C.neon} label={`CPA Reviewed ${item.cpaReviewedAt ? new Date(item.cpaReviewedAt).toLocaleDateString() : ''}`} />
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => startEdit(item)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, fontSize: 11,
                          fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
                          background: `${C.cyan}10`, color: C.cyan,
                          border: `1px solid ${C.cyan}30`,
                        }}
                      >Score</button>
                      {!item.cpaReviewed && (
                        <button
                          onClick={() => handleCpaReview(item.workItemId)}
                          disabled={mutating}
                          style={{
                            padding: '5px 12px', borderRadius: 6, fontSize: 11,
                            fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
                            background: `${C.neon}10`, color: C.neon,
                            border: `1px solid ${C.neon}30`,
                          }}
                        >CPA Review ✓</button>
                      )}
                    </div>
                  </div>

                  {/* Inline edit form */}
                  {editingId === item.workItemId && (
                    <div style={{ marginTop: 12, padding: 12, background: C.surface, borderRadius: 8, border: `1px solid ${C.faint}` }}>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4 }}>Uncertainty Score (1-5)</label>
                        <input
                          type="range" min={1} max={5} step={1}
                          value={editScore}
                          onChange={(e) => setEditScore(Number(e.target.value))}
                          style={{ width: '100%' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.dim }}>
                          <span>1 (Low)</span><span>2</span><span>3 (Medium)</span><span>4</span><span>5 (High)</span>
                        </div>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4 }}>Rationale</label>
                        <textarea
                          value={editRationale}
                          onChange={(e) => setEditRationale(e.target.value)}
                          rows={3}
                          style={{
                            width: '100%', padding: 8, borderRadius: 6, fontSize: 12,
                            background: C.bg, color: C.text, border: `1px solid ${C.faint}`,
                            fontFamily: F.display, resize: 'vertical', outline: 'none',
                          }}
                          placeholder="Describe the technical uncertainty..."
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => setEditingId(null)} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, background: 'none', color: C.muted, border: `1px solid ${C.faint}`, cursor: 'pointer' }}>Cancel</button>
                        <button onClick={saveEdit} disabled={mutating} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${C.violet}20`, color: C.violet, border: `1px solid ${C.violet}40`, cursor: 'pointer' }}>Save</button>
                      </div>
                    </div>
                  )}
                </GlassPanel>
              ))}
            </div>
          )}
        </>
      )}
    </EnginePageShell>
  );
}
